import { useRef, useState, useEffect } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import CalendarIcon from "assets/icons/calenar_sidebar.svg?react";
import moment from "moment";
import { DateRange } from "@mui/x-date-pickers-pro/models/range";
import { ENotificationType, notification } from "utils/notification";
import DropdownLogic from "components/DropdownLogic";
import { DateRangeCalendar } from "@mui/x-date-pickers-pro/DateRangeCalendar";
import {
  DatePicker,
  renderDigitalClockTimeView,
  TimePicker,
} from "@mui/x-date-pickers";
import { useOnClickOutside } from "usehooks-ts";
import { plPL } from "@mui/x-date-pickers-pro/locales";
import "moment/locale/pl";

import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import SecondCheckbox from "components/SecondCheckbox";
import { useTranslation } from "react-i18next";
moment.locale("pl");

const RangePicker: FC<Props> = ({
  label,
  defaultDates,

  onAccept,
  allowOneDate = false,
  onChangeIsFullDay,
  showAllDay = true,
  defaultAllDay = true,
}) => {
  const { t } = useTranslation();
  moment.locale("pl");
  const [showCalendar, setShowCalendar] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [, setPosition] = useState<"below" | "above" | "hidded">(
    "below"
  );

  const [selectedDates, setSelectedDates] = useState<
    [moment.Moment | null, moment.Moment | null]
  >([null, null]);

  const handleAcceptDate = (dateRange: DateRange<moment.Moment>) => {
    if (!dateRange[0] || (!dateRange[1] && !allowOneDate)) {
      notification({
        type: ENotificationType.ERROR,
        title: t("validation.badDateRangeTitle"),
        text: t("validation.provideDateRange"),
      });
      return;
    }

    if (!dateRange[0] && !dateRange[1]) {
      notification({
        type: ENotificationType.ERROR,
        title: t("validation.badDateRangeTitle"),
        text: t("validation.provideDateRange"),
      });
      return;
    }

    if (dateRange[0] && !dateRange[1] && allowOneDate) {
      dateRange[1] = dateRange[0];
    }

    // setShowCalendar(false);
    onAccept(dateRange);
  };

  useEffect(() => {
    if (selectedDates[0] || selectedDates[1]) {
      handleAcceptDate([selectedDates[0], selectedDates[1]]);
    }
  }, [selectedDates]);

  useEffect(() => {
    if (!showCalendar) {
      setPosition("hidded");
      return;
    }

    if (showCalendar && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (containerRect.bottom > viewportHeight) {
        setPosition("above");
      } else {
        setPosition("below");
      }
    }
  }, [showCalendar]);

  const ref = useRef(null);
  const [isModal, setIsModal] = useState(false);
  const [isFullDay, setIsFullDay] = useState(
    defaultAllDay &&
      (!defaultDates || defaultDates.length < 2 || !defaultDates[1])
  );

  useEffect(() => {
    onChangeIsFullDay?.(isFullDay);
  }, [isFullDay]);

  useOnClickOutside(ref, () => {
    if (!showCalendar || isModal) return;
    setShowCalendar(false);
  });

  const isSameDay =
    moment(selectedDates[0]).isSame(moment(selectedDates[1]), "day") ||
    (!selectedDates[0] && !selectedDates[1]);

  return (
    <div ref={ref} className={styles.wrapper}>
      <div
        onClick={() => {
          setShowCalendar(!showCalendar);
        }}
        className={styles.card}
      >
        <>
          <CalendarIcon className={styles.icon} />
          {label ?? t("columns.date")}
          <FontAwesomeIcon icon={showCalendar ? faCaretUp : faCaretDown} />
        </>
      </div>
      {showCalendar && (
        <DropdownLogic>
          <div className={`${styles.container}`}>
            <div className={styles.item}>
              <div className={styles.date}>
                <DatePicker
                  disableOpenPicker
                  format="DD/MM/YYYY"
                  onChange={(date) => {
                    setSelectedDates([date, selectedDates[1]]);
                  }}
                  value={
                    selectedDates?.[0] ? moment(selectedDates[0]) : undefined
                  }
                  sx={{
                    width: "100%",
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "standard",
                      label: t("rangePicker.dateFrom"),
                    },
                  }}
                />
              </div>
              {isFullDay && !isSameDay && (
                <div className={styles.time}>
                  <TimePicker
                    ampm={false}
                    viewRenderers={{
                      hours: renderDigitalClockTimeView,
                      minutes: renderDigitalClockTimeView,
                      seconds: renderDigitalClockTimeView,
                    }}
                    timeSteps={{ minutes: 10 }}
                    thresholdToRenderTimeInASingleColumn={10}
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "standard",
                        label: t("rangePicker.timeFrom"),
                      },
                    }}
                    onOpen={() => {
                      setIsModal(true);
                    }}
                    onClose={() => {
                      setIsModal(false);
                    }}
                    // disabled={!selectedDates?.[0]}
                    value={
                      selectedDates?.[0] ? moment(selectedDates[0]) : undefined
                    }
                    format="HH:mm"
                    onChange={(time) => {
                      const hour = moment(time).get("h");
                      const minutes = moment(time).get("m");
                      const newTime = moment(selectedDates[0])
                        .hour(hour)
                        .minute(minutes);
                      setSelectedDates([newTime, selectedDates[1]]);
                    }}
                  />
                </div>
              )}
            </div>
            {!isSameDay && (
              <div className={styles.item}>
                <div className={styles.date}>
                  <DatePicker
                    disableOpenPicker
                    format="DD/MM/YYYY"
                    onChange={(date) => {
                      setSelectedDates([selectedDates[0], date]);
                    }}
                    value={
                      selectedDates?.[1] ? moment(selectedDates[1]) : undefined
                    }
                    sx={{
                      width: "100%",
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "standard",
                        label: t("rangePicker.dateTo"),
                      },
                    }}
                  />
                </div>
                <div className={styles.time}>
                  <TimePicker
                    ampm={false}
                    viewRenderers={{
                      hours: renderDigitalClockTimeView,
                      minutes: renderDigitalClockTimeView,
                      seconds: renderDigitalClockTimeView,
                    }}
                    timeSteps={{ minutes: 10 }}
                    thresholdToRenderTimeInASingleColumn={10}
                    format="HH:mm"
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "standard",
                        label: t("rangePicker.timeTo"),
                      },
                    }}
                    onOpen={() => {
                      setIsModal(true);
                    }}
                    onClose={() => {
                      setIsModal(false);
                    }}
                    onChange={(time) => {
                      const hour = moment(time).get("h");
                      const minutes = moment(time).get("m");
                      const newTime = moment(
                        selectedDates[1] ?? selectedDates[0]
                      )
                        .hour(hour)
                        .minute(minutes);

                      setSelectedDates([selectedDates[0], newTime]);
                    }}
                    value={selectedDates?.[1] ? moment(selectedDates[1]) : null}
                  />
                </div>
              </div>
            )}

            {!isFullDay && (
              <div className={styles.time}>
                <TimePicker
                  ampm={false}
                  viewRenderers={{
                    hours: renderDigitalClockTimeView,
                    minutes: renderDigitalClockTimeView,
                    seconds: renderDigitalClockTimeView,
                  }}
                  timeSteps={{ minutes: 10 }}
                  thresholdToRenderTimeInASingleColumn={10}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "standard",
                      label: t("rangePicker.timeFrom"),
                    },
                  }}
                  onOpen={() => {
                    setIsModal(true);
                  }}
                  onClose={() => {
                    setIsModal(false);
                  }}
                  // disabled={!selectedDates?.[0]}
                  value={selectedDates?.[0] ? moment(selectedDates[0]) : null}
                  format="HH:mm"
                  onChange={(time) => {
                    const hour = moment(time).get("h");
                    const minutes = moment(time).get("m");
                    const newTime = moment(selectedDates[0])
                      .hour(hour)
                      .minute(minutes);
                    setSelectedDates([newTime, selectedDates[1]]);
                  }}
                />
                <div className={styles.separator}>-</div>
                <TimePicker
                  ampm={false}
                  viewRenderers={{
                    hours: renderDigitalClockTimeView,
                    minutes: renderDigitalClockTimeView,
                    seconds: renderDigitalClockTimeView,
                  }}
                  timeSteps={{ minutes: 10 }}
                  thresholdToRenderTimeInASingleColumn={10}
                  format="HH:mm"
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "standard",
                      label: t("rangePicker.timeTo"),
                    },
                  }}
                  onOpen={() => {
                    setIsModal(true);
                  }}
                  onClose={() => {
                    setIsModal(false);
                  }}
                  onChange={(time) => {
                    const hour = moment(time).get("h");
                    const minutes = moment(time).get("m");
                    const newTime = moment(selectedDates[1] ?? selectedDates[0])
                      .hour(hour)
                      .minute(minutes);

                    setSelectedDates([selectedDates[0], newTime]);
                  }}
                  value={selectedDates?.[1] ? moment(selectedDates[1]) : null}
                />
              </div>
            )}
            {showAllDay && (
              <div className={styles.checkbox}>
                <SecondCheckbox
                  onChange={() => {
                    if (!isSameDay) {
                      return;
                    }
                    setIsFullDay(!isFullDay);
                  }}
                  value={isFullDay}
                  text={t("calendar.allDay")}
                />
              </div>
            )}
            <LocalizationProvider
              localeText={
                plPL.components.MuiLocalizationProvider.defaultProps.localeText
              }
              dateAdapter={AdapterMoment}
              adapterLocale={"pl"}
            >
              <DateRangeCalendar
                showDaysOutsideCurrentMonth
                fixedWeekNumber={6}
                sx={{
                  "Mui-selected": {
                    background: "red",
                  },
                }}
                onChange={(d) => {
                  if (d[1]) {
                    setIsFullDay(true);
                  }

                  const startDate = d[0];
                  const endDate = d[1];

                  if (!endDate) {
                    d[0] = moment(startDate)
                      .set("hour", moment(new Date()).get("hour"))
                      .set("minutes", moment(new Date()).get("minutes"));
                    d[1] = moment(startDate)
                      .set("hour", moment(new Date()).get("hour"))
                      .set("minutes", moment(new Date()).get("minutes") + 30);
                  }
                  setSelectedDates(d);
                }}
                calendars={1}
                value={
                  defaultDates
                    ? [moment(defaultDates[0]), moment(defaultDates[1])]
                    : [null, null]
                }
              />
            </LocalizationProvider>
          </div>
        </DropdownLogic>
      )}
    </div>
  );
};

export default RangePicker;
